'use client';

import { useState, useCallback, useEffect } from 'react';

/**
 * Types of actions that can be performed on behalf of another agent
 */
export type OnBehalfOfAction = 'posting' | 'editing' | 'viewing';

/**
 * Context stored when acting on behalf of another agent
 */
export interface OnBehalfOfContext {
  /** The action being performed */
  action: OnBehalfOfAction;
  /** The agent ID we're acting on behalf of */
  agentId: string;
  /** The agent's display name */
  agentName: string;
  /** The agent's profile picture URL */
  profilePicture?: string | null;
  /** The agent's profile picture thumbnail URL */
  profilePictureThumbnail?: string | null;
  /** The agent's company name */
  companyName?: string | null;
  /** Property ID (for viewing/editing specific property) */
  propertyId?: string;
}

/**
 * Agent info used when starting an on-behalf-of action
 */
export interface AgentInfo {
  id: string;
  name: string;
  picture?: string | null;
  pictureThumbnail?: string | null;
  companyName?: string | null;
}

const STORAGE_KEY = 'onBehalfOfContext';

/**
 * The query flag that marks a non-admin URL as a deliberate admin hand-off.
 * `OnBehalfRouteGuard` clears the stored context on every other non-admin path,
 * and a surface that renders "viewing as" reads the same flag, so the two can
 * never disagree about what a hand-off looks like.
 */
export const ADMIN_ON_BEHALF_FLAG = 'from_admin_on_behalf';

/**
 * Puts the hand-off flag on a path, keeping any query the path already carries
 * byte for byte. Every navigation that must keep acting for an agent goes through
 * this, and a path may pass through more than one of them, so it is idempotent: a
 * path that already carries the flag comes back untouched rather than with a
 * second copy the server would see as `?flag=1&flag=1`. A fragment ends a URL, so
 * the flag is inserted in front of it, never appended after it.
 *
 * @param {string} path - An app path, with or without a query or a `#fragment`
 * @return {string} The same path carrying `from_admin_on_behalf=1`
 */
export const withAdminOnBehalfFlag = (path: string) => {
  const fragmentAt = path.indexOf('#');
  const base = fragmentAt === -1 ? path : path.slice(0, fragmentAt);
  const fragment = fragmentAt === -1 ? '' : path.slice(fragmentAt);
  if (new URLSearchParams(base.split('?')[1] ?? '').has(ADMIN_ON_BEHALF_FLAG)) {
    return path;
  }
  return `${base}${
    base.includes('?') ? '&' : '?'
  }${ADMIN_ON_BEHALF_FLAG}=1${fragment}`;
};

/**
 * Hook for managing "on behalf of" functionality across the admin panel.
 *
 * This hook provides a unified way to:
 * - Store context about which agent we're acting for
 * - Check if we're currently acting on behalf of someone
 * - Get the API payload needed for on-behalf-of requests
 *
 * The context is persisted in sessionStorage to survive page navigation.
 *
 * @example
 * ```tsx
 * const {
 *   context,
 *   isActingOnBehalf,
 *   startActingFor,
 *   clearContext,
 *   getApiPayload
 * } = useOnBehalfOf();
 *
 * // Start acting for another agent
 * startActingFor('posting', { id: 'agent-123', name: 'John Doe' });
 *
 * // Include in API calls
 * await createProperty({ ...data, ...getApiPayload() });
 *
 * // Check state
 * if (isActingOnBehalf) {
 *   console.log(`Acting for ${context?.agentName}`);
 * }
 *
 * // Clear when done
 * clearContext();
 * ```
 */
export function useOnBehalfOf() {
  // Initialize state from sessionStorage (synchronously to avoid hydration issues)
  const [context, setContext] = useState<OnBehalfOfContext | null>(() => {
    if (typeof window === 'undefined') return null;

    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    return null;
  });

  // Sync state if sessionStorage changes externally (e.g., in another tab)
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          setContext(JSON.parse(stored));
        } catch {
          setContext(null);
        }
      } else {
        setContext(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /**
   * Start acting on behalf of an agent
   */
  const startActingFor = useCallback(
    (action: OnBehalfOfAction, agent: AgentInfo, propertyId?: string) => {
      const ctx: OnBehalfOfContext = {
        action,
        agentId: agent.id,
        agentName: agent.name,
        profilePicture: agent.picture,
        profilePictureThumbnail: agent.pictureThumbnail,
        companyName: agent.companyName,
        propertyId,
      };

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
      setContext(ctx);
    },
    []
  );

  /**
   * Clear the on-behalf-of context
   */
  const clearContext = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setContext(null);
  }, []);

  /**
   * Get the API payload for on-behalf-of requests
   * Returns an object with on_behalf_of_agent_id if context exists
   */
  const getApiPayload = useCallback(() => {
    if (!context) return {};
    return { on_behalf_of_agent_id: context.agentId };
  }, [context]);

  /**
   * Update the current context (e.g., to change action type or property)
   */
  const updateContext = useCallback(
    (updates: Partial<OnBehalfOfContext>) => {
      if (!context) return;

      const updatedContext = { ...context, ...updates };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedContext));
      setContext(updatedContext);
    },
    [context]
  );

  return {
    /** The current on-behalf-of context, or null if not acting for anyone */
    context,
    /** Whether we're currently acting on behalf of another agent */
    isActingOnBehalf: !!context,
    /** The current action type (posting, editing, viewing) */
    action: context?.action,
    /** The agent ID we're acting for */
    targetAgentId: context?.agentId,
    /** The agent name we're acting for */
    targetAgentName: context?.agentName,
    /** The property ID if one is set */
    targetPropertyId: context?.propertyId,
    /** Start acting for an agent */
    startActingFor,
    /** Clear the on-behalf-of context */
    clearContext,
    /** Update the current context */
    updateContext,
    /** Get the API payload for on-behalf-of requests */
    getApiPayload,
  };
}

/**
 * Utility to check if on-behalf-of context exists without using the hook
 * Useful for server-side checks or outside React components
 */
export function hasOnBehalfOfContext(): boolean {
  if (typeof window === 'undefined') return false;
  return !!sessionStorage.getItem(STORAGE_KEY);
}

/**
 * Utility to get on-behalf-of context without using the hook
 * Useful for initial state in components
 */
export function getOnBehalfOfContext(): OnBehalfOfContext | null {
  if (typeof window === 'undefined') return null;

  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Utility to clear on-behalf-of context without using the hook
 * Useful in cleanup functions or outside React components
 */
export function clearOnBehalfOfContext(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(STORAGE_KEY);
}
